import { useRef, useEffect } from 'react';
import { TOPICS } from '../data/topics';
import { useProgressTracker } from '../hooks/useProgressTracker';
import QuizPlayer from './QuizPlayer';

const CALLOUT_CLS = { info: '', warning: 'amber', tip: 'green', formula: 'red' };

export default function LessonView({ topicKey, subtopic, lessonData, fromCache, onRegenerate }) {
  const quizRef = useRef(null);
  const { completeLesson } = useProgressTracker();
  const t = TOPICS[topicKey];

  // Mark lesson as completed when viewed
  useEffect(() => {
    if (topicKey && subtopic) {
      completeLesson(topicKey, subtopic);
    }
  }, [topicKey, subtopic, completeLesson]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-up">
      {/* Main lesson card */}
      <div className="lesson-card">
        <div className="lesson-card-header">
          <div>
            <div className="lesson-module-tag">{`// ${(lessonData.module || t.label).toUpperCase()}`}</div>
            <div className="lesson-title">{lessonData.title || subtopic}</div>
            <div className="lesson-meta-row">
              <span className="lesson-chip">{lessonData.duration || '~15 min read'}</span>
              {lessonData._source === 'phak_ch3'
                ? <span className="lesson-chip" style={{ color: 'var(--amber)', borderColor: 'rgba(251,191,36,0.3)' }}>📖 PHAK Ch.3</span>
                : lessonData._source === 'phak_ch1'
                ? <span className="lesson-chip" style={{ color: 'var(--amber)', borderColor: 'rgba(251,191,36,0.3)' }}>📖 PHAK Ch.1</span>
                : lessonData._source === 'bundled'
                ? <span className="lesson-chip" style={{ color: 'var(--purple)', borderColor: 'rgba(167,139,250,0.3)' }}>📦 Offline</span>
                : <>
                    <span className="lesson-chip" style={{ color: 'var(--blue)', borderColor: 'rgba(96,165,250,0.3)' }}>✦ AI Generated</span>
                    {fromCache && <span className="lesson-chip" style={{ color: 'var(--green)', borderColor: 'rgba(0,214,143,0.3)' }}>⚡ Cached</span>}
                  </>
              }
              <span className="lesson-chip" style={{ color: t.color, borderColor: `${t.color}40` }}>{t.icon} {t.label.split('&')[0].trim()}</span>
            </div>
          </div>
          {!lessonData._source?.startsWith('phak_') && (
            <button className="lesson-regen-btn" onClick={onRegenerate}>↻ Regenerate</button>
          )}
        </div>

        <div className="lesson-body">
          {/* Text sections */}
          <div className="lesson-text-col">
            {(lessonData.sections || []).map((s, i) => (
              <div key={i}>
                <h3>{s.heading}</h3>
                <p dangerouslySetInnerHTML={{ __html: s.content }} />
                {s.callout && (
                  <div className={`callout ${CALLOUT_CLS[s.callout.type] || ''}`}>
                    {s.callout.text}
                  </div>
                )}
                {s.diagram && (
                  <div className="diagram-block">
                    <div className="diagram-label">{s.diagram.label}</div>
                    {s.diagram.art}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Glossary */}
          <div className="lesson-glossary-col">
            <div className="glossary-title">Key Terms</div>
            {(lessonData.glossary || []).map((g) => (
              <div key={g.term} className="glossary-item">
                <div className="glossary-term">{g.term}</div>
                <div className="glossary-def">{g.def}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Checkpoint */}
      {lessonData.checkpoint && (
        <div className="checkpoint-card">
          <div className="checkpoint-icon">📍</div>
          <div>
            <div className="checkpoint-label">{"// CHECKPOINT"}</div>
            <div className="checkpoint-title">{lessonData.checkpoint.title}</div>
            <div className="checkpoint-sub">{(lessonData.checkpoint.points || []).join(' · ')}</div>
          </div>
          <button className="checkpoint-btn" onClick={() => quizRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
            Take Quiz →
          </button>
        </div>
      )}

      {/* Lesson quiz */}
      {(lessonData.quiz || []).length > 0 && (
        <div className="lesson-quiz-card" ref={quizRef}>
          <div className="lqc-header">
            <div>
              <div className="lqc-title">Lesson Quiz</div>
              <div className="lqc-sub">{lessonData.quiz.length} questions based on this lesson</div>
            </div>
            <span className="lesson-chip" style={{ marginLeft: 'auto' }}>AI-Generated</span>
          </div>
          <div className="lqc-body">
            <QuizPlayer questions={lessonData.quiz} topicKey={topicKey} subtopic={subtopic} />
          </div>
        </div>
      )}
    </div>
  );
}
